import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  SimpleChanges, OnChanges,
} from '@angular/core';
import {
  ArcElement,
  CategoryScale,
  Chart,
  DoughnutController,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from 'chart.js';

@Component({
  selector: 'app-chart',
  imports: [],
  template: ` <div
    class="tw-w-full tw-flex tw-flex-col tw-items-center tw-pt-2 tw-rounded-xl"
    [class.chart-container]="config.showBackground"
  >
    <div
      [style.height]="config.height"
      [style.width]="config.width"
      class="tw-my-2"
    >
      <canvas role="img" [attr.id]="chartId"></canvas>
    </div>
    <div
      class="tw-flex tw-flex-col tw-font-bold tw-tracking-wide app-color-slate tw-uppercase tw-text-[13px] tw-pt-2"
    >
      @if (config.showLabels) {
        @for (series of config.series; track series) {
          <div class="tw-flex tw-items-center">
            <div class="tw-flex-none app-pr2 tw-mt-1">
              <!-- <mat-icon class="legend-icon" [style.color]="series.color"
            >circle</mat-icon
          > -->
            </div>
            <div class="tw-flex-grow">
              {{ series.value }} - {{ series.label }}
            </div>
          </div>
        }
      }
    </div>
  </div>`,
  styles: `
    .chart-container {
      --background: radial-gradient(
        circle at 100% 10%,
        #000000,
        #040404,
        #080808,
        #0c0c0c,
        #101010,
        #131313,
        #151515,
        #181818,
        #1a1a1a,
        #1c1c1c,
        #1f1f1f,
        #212121
      );
      background-color: var(--sed-open-grey-color);
      --background-color: var(--sed-black-80-color);
      border-bottom: 8px solid var(--sed-grey-100-color);
    }
  `,
})
export class ChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  @Input()
  config: ChartConfig;

  @Input()
  tooltipUnitLabel = '';

  @Input()
  tooltipPrefixLabel = '';

  chartId: string;

  private chart: any;

  constructor() {
    this.chartId = `chart-${Math.floor(Math.random() * 1000000)}`;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && !changes['config'].isFirstChange()) {
      this.chart?.destroy();
      this.createChart();
    }
  }

  ngAfterViewInit(): void {
    this.createChart();
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  captureImage(): string | null {
    if (!this.chart) {
      return null;
    }

    return this.chart.toBase64Image('image/png', 1);
  }

  private createChart(): void {
    Chart.register(
      DoughnutController,
      CategoryScale,
      LinearScale,
      ArcElement,
      Legend,
      Title,
      Tooltip,
    );

    const labels = this.config.series.map((x) => x.label);
    const values = this.config.series.map((x) => x.value);
    const colors = this.config.series.map((x) => x.color);

    this.chart = new Chart(this.chartId, {
      type: 'doughnut',

      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: colors,
            borderRadius: 8,
          },
        ],
      },
      options: {
        animation: true,
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: 2,
        },
        cutout: '65%',
        elements: {
          arc: {
            borderWidth: 0,
            spacing: 3,
          },
        },
        plugins: {
          legend: {
            display: false,
            position: 'bottom',
            labels: {
              font: {
                size: 14,
                weight: 'bold',
                family: 'Arial',
              },
            },
          },
          title: {
            display: false,
            text: '',
          },
          tooltip: {
            borderWidth: 200,
            callbacks: {
              label: (context) => {
                return `${this.tooltipPrefixLabel}${context.formattedValue} ${this.tooltipUnitLabel}`;
              },
            },
          },
        },
      },
    });
  }
}

export class ChartConfig {
  showLabels = true;

  showBackground = true;

  height = '150px';

  width = '150px';

  series: ChartSeries[] = [];

  addSeries(label: string, value: number, color: string): ChartConfig {
    this.series.push(new ChartSeries(label, value, color));
    return this;
  }
}

export class ChartSeries {
  constructor(
    public label: string,
    public value: number,
    public color: string,
  ) {}
}
